import type {
    InternalEligibilityResponse,
    LegacyEligibilityStatus
} from '../../hooks/use-eligibility-verifier';

// Shared scaffolding for provider-scoped eligibility comparison.
//
// Each provider owns a model (the fields its downstream consumer actually
// reads), the extractors that pull that model off each response, and the
// compare mode per field. Nothing here knows about any specific provider.

// ---------------------------------------------------------------------------
// Where each surface reports its provider
// ---------------------------------------------------------------------------

// These two paths are wrapper-level and provider-independent. What the strings
// at the end of them *mean* is each provider model's business — there is no
// shared table of provider names anywhere in this file, so adding a provider
// never touches shared code.

// `eligibilitySource` is the top-level field and the primary signal — it reports
// the provider (e.g. `ESI`) and survives `expand=false`, which drops
// `coverageInfo.coverageDetails` entirely. `apiSource` is the same concept nested
// inside the coverage details, and is what the ESI invoice writer asserts on, so
// it stands in when the top-level field is absent.
export const legacyProviderString = (
  payload: LegacyEligibilityStatus,
): string | undefined =>
  payload.eligibilitySource ??
  payload.coverageInfo?.coverageDetails?.apiSource;

export const internalProviderString = (
  payload: InternalEligibilityResponse,
): string | undefined => payload.states?.verificationState?.provider;

// ---------------------------------------------------------------------------
// Rows
// ---------------------------------------------------------------------------

export type DiffStatus =
  | 'match'
  | 'mismatch'
  | 'missingLegacy'
  | 'missingInternal'
  | 'missingBoth'
  | 'informational';

// The six statuses partition into "the two paths agree" and "they don't". A
// field present on one side and absent on the other is disagreement just as
// much as two differing values — it means one path would hand the writer a
// field the other wouldn't. `missingBoth` is agreement (neither path has it),
// and `informational` carries no verdict at all.
//
// Lives here rather than in the page so the headline count can't drift from the
// row statuses it summarizes.
export const isDivergent = (status: DiffStatus): boolean =>
  status === 'mismatch' ||
  status === 'missingLegacy' ||
  status === 'missingInternal';

export type CompareMode =
  // byte-for-byte after trimming
  | 'exact'
  // trimmed, case-folded; for fields a provider returns in varying case
  | 'caseInsensitive'
  // shown side by side, never given a verdict
  | 'informational';

export interface DiffRow {
  key: string;
  label: string;
  legacy?: string;
  internal?: string;
  status: DiffStatus;
  note?: string;
  // True for the handful of rows that carry no PHI (e.g. the eligibility
  // headline booleans) — the page's PHI blur toggle skips these regardless of
  // its own state.
  neverBlur?: boolean;
  // Which downstream writer the row feeds, for sections where fields differ per
  // writer. Unset for ordinary rows.
  appliesTo?: string;
}

// A provider model is a flat record of stringified fields. Declare models with
// `type`, not `interface` — only type aliases get the implicit index signature
// that satisfies this constraint.
export type ProviderModel = Record<string, string | undefined>;

export interface FieldSpec<TModel extends ProviderModel> {
  key: keyof TModel & string;
  label: string;
  compare: CompareMode;
  note?: string;
  // Plan/administrative fields only — see DiffRow.neverBlur. Unset is the safe
  // default.
  neverBlur?: boolean;
  appliesTo?: string;
}

// Normalizes to a trimmed non-empty string or undefined, so "absent" is one
// concept downstream instead of three (null, undefined, '').
export const present = (value: unknown): string | undefined => {
  if (value === null || value === undefined) return undefined;
  const text = String(value).trim();
  return text === '' ? undefined : text;
};

export const buildRows = <TModel extends ProviderModel>(
  fields: readonly FieldSpec<TModel>[],
  legacy: TModel,
  internal: TModel,
): DiffRow[] =>
  fields.map(({ key, label, compare, note, neverBlur, appliesTo }) => {
    const l = legacy[key];
    const i = internal[key];
    let status: DiffStatus;
    if (l === undefined && i === undefined) status = 'missingBoth';
    else if (l === undefined) status = 'missingLegacy';
    else if (i === undefined) status = 'missingInternal';
    else if (compare === 'informational') status = 'informational';
    else if (compare === 'caseInsensitive') {
      status = l.toLowerCase() === i.toLowerCase() ? 'match' : 'mismatch';
    } else status = l === i ? 'match' : 'mismatch';
    return {
      key,
      label,
      legacy: l,
      internal: i,
      status,
      note,
      neverBlur,
      appliesTo,
    };
  });

// ---------------------------------------------------------------------------
// Provider contract
// ---------------------------------------------------------------------------

// The model each side projects to — what this provider's downstream consumer
// would actually receive from each eligibility path. Kept as the erased
// ProviderModel so the shared layer never learns a provider's field names.
export interface ProviderProjections {
  legacy: ProviderModel;
  internal: ProviderModel;
}

// Everything a provider must declare to be loadable. A model owns its identity,
// the wire spellings it answers to, its field set, and how it compares — the
// shared layer only routes.
export interface ProviderComparator {
  // Display name, owned by the provider module.
  provider: string;
  // The two surfaces spell the same provider differently: legacy sends `ESI`,
  // internal sends `ELIGIBILITY_PROVIDER_ESI`. Each provider declares both sets
  // so no shared lookup table has to know about it. Repeat a spelling in only
  // one provider — two models claiming the same string is a bug the registry
  // cannot detect for you.
  legacyApiSources: readonly string[];
  internalProviders: readonly string[];
  // Verdict rows for this provider's model. Free to ignore buildRows entirely
  // if a provider's payload doesn't fit a flat field walk.
  compare: (
    legacy: LegacyEligibilityStatus,
    internal: InternalEligibilityResponse,
  ) => DiffRow[];
  // Provider-specific context shown beside the verdict rows but never given a
  // verdict — e.g. the several unrelated notions of "status" ESI carries.
  context: (
    legacy: LegacyEligibilityStatus,
    internal: InternalEligibilityResponse,
  ) => DiffRow[];
  // Extra-column rows for writers that read provider-specific roster columns.
  // Shown in their own section and kept out of the headline divergence count.
  extraColumns?: (
    legacy: LegacyEligibilityStatus,
    internal: InternalEligibilityResponse,
  ) => DiffRow[];
  // The two projected models themselves. The diff rows are derived from these,
  // so this is the source, not a reconstruction of it — the rows can't be
  // inverted back into a model (they carry non-model rows like the eligibility
  // headline).
  project: (
    legacy: LegacyEligibilityStatus,
    internal: InternalEligibilityResponse,
  ) => ProviderProjections;
}

// ---------------------------------------------------------------------------
// Provider-independent headline
// ---------------------------------------------------------------------------

// Both wrappers carry an eligibility flag regardless of provider. The internal
// contract has no single overarching boolean, but billing's answer drifting
// mid-migration is a hard failure however many field rows agree.
export const eligibilityRow = (
  legacy: LegacyEligibilityStatus,
  internal: InternalEligibilityResponse,
): DiffRow => ({
  key: 'isEligible',
  label: 'isEligible / isBillingEligible',
  legacy: String(legacy.isEligible),
  internal: String(internal.isBillingEligible),
  status:
    legacy.isEligible === internal.isBillingEligible ? 'match' : 'mismatch',
  note: 'Disagreement here blocks the migration regardless of field-level matches.',
  neverBlur: true,
});
